import { createRouter, createWebHistory } from 'vue-router';
import routes from './routes.js';

import { useNotificationStore } from '@/stores/notification.js';
import { useUserStore } from '@/stores/user';
import { useSiteStore } from '@/stores/site';
import { useEmployeesStore } from '@/stores/employees';
import { useRoleStore } from '@/stores/role';
import { useAdminStore } from '@/stores/admin';
import app from '@/main.js';

/**
 * Admin company workflow: routes that show a specific company by hash must set
 * `meta.adminCompanyContext: true` (see `routes.js` under `/admin` children).
 *
 * Company hash param:
 * - Default: `route.params.companyHash` then `route.params.hash` (covers
 *   AdminAuditOverview and Company Detail).
 * - Nonstandard param name: set `meta.adminCompanyHashParam` on that route to
 *   the param key (e.g. `meta: { adminCompanyContext: true, adminCompanyHashParam: 'orgHash' }`).
 */
const getAdminCompanyWorkflowHash = (route) => {
    const withMeta = route?.matched?.filter((r) => r.meta?.adminCompanyContext);
    const record =
        withMeta?.length ? withMeta[withMeta.length - 1] : null;
    if (!record) return null;

    const paramKey = record.meta.adminCompanyHashParam;
    const raw =
        paramKey != null && paramKey !== ''
            ? route.params[paramKey]
            : route.params.companyHash ?? route.params.hash;
    if (raw == null || raw === '') return null;
    const s = String(raw).trim();
    return s || null;
};

const clearAdminCompanyWorkflowContextOnLeave = (toHash, fromHash) => {
    const adminStore = useAdminStore();

    if (!toHash && fromHash && adminStore.workflowCompanyHash) {
        adminStore.clearCompanyWorkflow();
    }
};

const ensureAdminCompanyWorkflowContextBeforeMount = async (toHash, fromHash) => {
    if (!toHash) return;

    const adminStore = useAdminStore();
    const needsEnsure =
        toHash !== fromHash ||
        adminStore.workflowCompanyHash !== toHash ||
        !Number.isInteger(Number(adminStore.companyId)) ||
        Number(adminStore.companyId) <= 0;

    if (needsEnsure) {
        await adminStore.ensureCompanyWorkflowFromHash(toHash);
    }
};

// Keeps the user's pre-admin site selection in memory so we can restore it on exit.
let preAdminGlobalSiteId = null;

// When true, ajax layer must NOT auto-attach global site_id to requests.
// This prevents `/admin` bootstrap and admin APIs from being unintentionally scoped.
let suppressGlobalSiteScope = false;

export const shouldSuppressGlobalSiteScope = () => suppressGlobalSiteScope;

const isAdminPath = (route) => route?.path?.startsWith('/admin');

const hasValue = (value) => value !== null && value !== undefined;

const syncAdminGlobalSiteScopeBeforeNavigation = (to, from) => {
    const toAdmin = isAdminPath(to);
    const fromAdmin = isAdminPath(from);

    // Toggle request-level site scoping as early as possible so ajax requests in
    // this navigation cycle know whether to include/exclude global site_id.
    suppressGlobalSiteScope = toAdmin;

    // Only capture backup when crossing boundary into /admin.
    // We do not mutate store here to avoid side-effect fetches in the old page.
    const enteringAdmin = !fromAdmin && toAdmin;
    if (!enteringAdmin) return;

    const siteStore = useSiteStore();
    const currentGlobalSiteId = siteStore.globalSiteId;
    const storedGlobalSiteId = localStorage.getItem('globalSiteId');

    preAdminGlobalSiteId = hasValue(currentGlobalSiteId)
        ? currentGlobalSiteId
        : storedGlobalSiteId ?? null;
};

const syncAdminGlobalSiteScopeAfterNavigation = (to, from) => {
    const toAdmin = isAdminPath(to);
    const fromAdmin = isAdminPath(from);

    const enteringAdmin = !fromAdmin && toAdmin;
    const leavingAdmin = fromAdmin && !toAdmin;

    // Re-assert request suppression from the confirmed route (important for redirects).
    suppressGlobalSiteScope = toAdmin;

    // Apply state mutation only after route commit so the previous page doesn't
    // react to intermediate globalSiteId changes during navigation.
    if (enteringAdmin) {
        // Explicitly clear selected site while in admin panel to avoid UI/data
        // side effects caused by stale site selection.
        useSiteStore().setGlobalSiteId(null);
        return;
    }

    if (leavingAdmin && hasValue(preAdminGlobalSiteId)) {
        useSiteStore().setGlobalSiteId(preAdminGlobalSiteId);
        preAdminGlobalSiteId = null;
        return;
    }

    if (leavingAdmin) {
        preAdminGlobalSiteId = null;
    }
};

const router = createRouter({
    history: createWebHistory(process.env.BASE_URL),
    scrollBehavior(to, from, savedPosition) {
        if (savedPosition) {
            return savedPosition;
        }

        // if the route is the same, don't scroll
        if (to.name === from.name) {
            return false;
        }

        const rightPanel = document.getElementById('app-right-panel');
        if (rightPanel) {
            try {
                rightPanel.scrollTo({
                    top: 0,
                    behavior: 'instant', // Modern browsers
                });
            } catch (e) {
                // Fallback for browsers that don't support 'instant'
                rightPanel.scrollTop = 0;
            }
        }
    },
    routes,
});

/* Removed
router.beforeEach((to, from, next) => {

    const isAuthenticated = ('token' in localStorage)
    const routeRequiresAuthentication = !('requiresAuth' in to.meta) || to.meta.requiresAuth

    // require auth unless otherwise specified in meta.requiresAuth
    if (!routeRequiresAuthentication || isAuthenticated)
        next()
    else
        next({ name: 'Login' })
})
*/
// ... existing code ...

router.beforeEach(async (to, from) => {
    // Capture pre-admin global site selection before any async route work starts.
    // Important: this only backs up state; actual clear/restore happens in afterEach
    // to avoid triggering in-between fetches on the route we're leaving.
    syncAdminGlobalSiteScopeBeforeNavigation(to, from);


    const userStore = useUserStore();

    // Allow access to public routes immediately
    if (to.meta.requiresAuth === false) {
        return true;
    }

    // For protected routes, check authentication
    try {
        // Always perform sync to ensure we have latest user data and wait for it to complete
        await new Promise((resolve) => {
            userStore.sync(() => {
                resolve();
            });
        });

        // Check if user is authenticated after sync, if not redirect to login
        if (userStore.isAuthenticated && userStore.user) {
            const toAdminCompanyHash = getAdminCompanyWorkflowHash(to);
            const fromAdminCompanyHash = getAdminCompanyWorkflowHash(from);

            clearAdminCompanyWorkflowContextOnLeave(
                toAdminCompanyHash,
                fromAdminCompanyHash
            );

            if (toAdminCompanyHash) {
                await ensureAdminCompanyWorkflowContextBeforeMount(
                    toAdminCompanyHash,
                    fromAdminCompanyHash
                );
            } else {
                // if user is authenticated fetch sites, then fetch employees based on current site and sync it
                const siteStore = useSiteStore();
                await siteStore.initializeSiteStore();

                const employeesStore = useEmployeesStore();
                // wait to load all the employees before loading the page
                await new Promise((resolve) => {
                    employeesStore.sync(() => {
                        resolve();
                    });
                });

                const roleStore = useRoleStore();
                await roleStore.syncRoles();
            }

            return true;
        } else {
            // If the user is not authenticated, redirect to login with the current path as the redirect query
            return { name: 'Login', query: { redirect: to.fullPath }, replace: true };
        }
    } catch (error) {
        // On error, redirect to login with the current path as the redirect query
        return { name: 'Login', query: { redirect: to.fullPath }, replace: true };
    }
});

let lastFetchTime = 0; // Initialize to 0 so it runs on first route change

router.afterEach((to, from) => {
    // Apply admin site-scope mutation after route confirmation.
    // Clear selected site in admin; restore previous selection when leaving admin.
    syncAdminGlobalSiteScopeAfterNavigation(to, from);
    clearAdminCompanyWorkflowContextOnLeave(
        getAdminCompanyWorkflowHash(to),
        getAdminCompanyWorkflowHash(from)
    );
});

router.beforeEach((to, _from, next) => {
    if (!app) return next();

    const userStore = useUserStore();
    if (to.meta.requiresAuth === false || !userStore.isAuthenticated) {
        return next();
    }

    const notificationStore = useNotificationStore();
    if (!notificationStore) return next();

    const now = Date.now();

    if (now - lastFetchTime > 30000) {
        // 30 seconds = 30000 milliseconds
        notificationStore.sync();
        lastFetchTime = now;
    }

    next();
});

export default router;
